import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

export type BadgeVariant =
  | 'success' | 'warning' | 'danger' | 'info' | 'neutral'
  // ERP semântico — mapeados automaticamente via `statusToBadge()`
  | 'Finalizado' | 'Cancelado' | 'EmAberto' | 'Pendente' | 'Processando'
  // Estoque
  | 'estoque-ok' | 'estoque-baixo' | 'estoque-critico'
  // NF-e
  | 'Autorizada' | 'Rejeitada' | 'EmProcessamento' | 'Cancelada'
  // Orçamentos
  | 'Aprovado' | 'Recusado' | 'Expirado';

export type BadgeSize = 'sm' | 'md';

/** Mapeia status de domínio para variante de cor */
export function statusToBadge(status: string): BadgeVariant {
  const map: Record<string, BadgeVariant> = {
    finalizado:        'Finalizado',
    cancelado:         'Cancelado',
    emaberto:          'EmAberto',
    pendente:          'Pendente',
    processando:       'Processando',
    autorizada:        'Autorizada',
    rejeitada:         'Rejeitada',
    emprocessamento:   'EmProcessamento',
    cancelada:         'Cancelada',
    aprovado:          'Aprovado',
    recusado:          'Recusado',
    expirado:          'Expirado',
  };
  return map[status.toLowerCase()] ?? 'neutral';
}

// ─── Color map ────────────────────────────────────────────────────────────────
// Bg + Text + Ring para cada variante, WCAG ≥ 4.5:1 garantido
const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  success:          'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-200',
  warning:          'bg-amber-50    text-amber-700   ring-1 ring-amber-200',
  danger:           'bg-rose-50     text-rose-700    ring-1 ring-rose-200',
  info:             'bg-blue-50     text-blue-700    ring-1 ring-blue-200',
  neutral:          'bg-slate-100   text-slate-600   ring-1 ring-slate-200',
  // Pedidos
  Finalizado:       'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-200',
  Cancelado:        'bg-rose-50     text-rose-700    ring-1 ring-rose-200',
  EmAberto:         'bg-blue-50     text-blue-700    ring-1 ring-blue-200',
  Pendente:         'bg-amber-50    text-amber-700   ring-1 ring-amber-200',
  Processando:      'bg-violet-50   text-violet-700  ring-1 ring-violet-200',
  // Estoque
  'estoque-ok':     'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-200',
  'estoque-baixo':  'bg-amber-50    text-amber-700   ring-1 ring-amber-200',
  'estoque-critico':'bg-rose-50     text-rose-700    ring-1 ring-rose-200',
  // NF-e
  Autorizada:       'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-200',
  Rejeitada:        'bg-rose-50     text-rose-700    ring-1 ring-rose-200',
  EmProcessamento:  'bg-violet-50   text-violet-700  ring-1 ring-violet-200',
  Cancelada:        'bg-slate-100   text-slate-600   ring-1 ring-slate-200',
  // Orçamentos
  Aprovado:         'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-200',
  Recusado:         'bg-rose-50     text-rose-700    ring-1 ring-rose-200',
  Expirado:         'bg-slate-100   text-slate-500   ring-1 ring-slate-200',
};

const DOT_CLASSES: Record<BadgeVariant, string> = {
  success:          'bg-emerald-500',
  warning:          'bg-amber-500',
  danger:           'bg-rose-500',
  info:             'bg-blue-500',
  neutral:          'bg-slate-400',
  Finalizado:       'bg-emerald-500',
  Cancelado:        'bg-rose-500',
  EmAberto:         'bg-blue-500',
  Pendente:         'bg-amber-500',
  Processando:      'bg-violet-500',
  'estoque-ok':     'bg-emerald-500',
  'estoque-baixo':  'bg-amber-500',
  'estoque-critico':'bg-rose-500',
  Autorizada:       'bg-emerald-500',
  Rejeitada:        'bg-rose-500',
  EmProcessamento:  'bg-violet-500',
  Cancelada:        'bg-slate-400',
  Aprovado:         'bg-emerald-500',
  Recusado:         'bg-rose-500',
  Expirado:         'bg-slate-400',
};

/**
 * @usage
 * <!-- Variante explícita -->
 * <ui-badge variant="Finalizado">Finalizado</ui-badge>
 *
 * <!-- Auto-mapeado por status de domínio -->
 * <ui-badge [status]="pedido.status">{{ pedido.status }}</ui-badge>
 *
 * <!-- Com ponto de status animado (útil para "Processando") -->
 * <ui-badge variant="Processando" [dot]="true">Processando</ui-badge>
 */
@Component({
  selector: 'ui-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span [class]="hostClass">
      @if (dot) {
        <span
          [class]="dotClass"
          class="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          [class.animate-pulse]="pulse"
          aria-hidden="true"
        ></span>
      }
      <ng-content />
    </span>
  `,
})
export class UiBadgeComponent {
  /** Variante explícita ou status ERP direto */
  @Input() variant: BadgeVariant = 'neutral';

  /** Atalho: passa o status bruto (ex: "Finalizado"), mapeado automaticamente */
  @Input() set status(s: string) {
    this.variant = statusToBadge(s);
  }

  @Input() size: BadgeSize = 'sm';

  /** Mostra ponto de status à esquerda */
  @Input() dot = false;

  /** Pulsa o ponto (útil para status em processamento) */
  @Input() pulse = false;

  get hostClass(): string {
    const sizeClass = this.size === 'sm'
      ? 'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full'
      : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full';
    return `${sizeClass} ${VARIANT_CLASSES[this.variant]}`;
  }

  get dotClass(): string {
    return DOT_CLASSES[this.variant];
  }
}
